import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import { LocalizationProvider, PickersDayProps, StaticDatePicker } from '@mui/x-date-pickers-pro'
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns'
import { addWeeks, endOfWeek, format, isWithinInterval, startOfWeek, subWeeks } from 'date-fns'
import { enqueueSnackbar } from 'notistack'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { CustomPickersDay } from '@components/multiple_date_picker/custom_picker_day'
import { useBookingContext } from '@providers/booking'
import { buildErrorMessage } from '@shared/error'
import { i18WithParams as t } from '@shared/locale'
import { DayCalendarSkeleton } from 'components-ui/src/date_picker_skeleton/date_picker_skeleton'
import { hasRequestTime } from 'components-ui/src/shared/location'
import { Services, Types } from 'service-api'
import { captureError } from 'service-api/src/shared'
import { TPetOffers } from '../types'
import { buildGroomPetGroup, getDateRange, isDataInCache, isDayAvailable } from './helper'

export const DatePicker = ({
  maxDate,
  minDate,
  startDate,
  groomOffer,
  checkProvider,
}: {
  startDate: Date
  maxDate: Date
  minDate: Date
  groomOffer: TPetOffers
  checkProvider: boolean
}) => {
  const bookingCtx = useBookingContext()
  const { state } = bookingCtx
  const { accountName: locationName = '' } = useParams()
  const serviceTypeName = state.serviceTypeName
  const isFetching = useRef<boolean>(false)
  const [isLoading, setIsLoading] = useState(true)
  const [weekStart, setWeekStart] = useState(startOfWeek(startDate, { weekStartsOn: 0 }))
  const [requestDates, setRequestDates] = useState<Date[]>(() => {
    return getDateRange(weekStart, minDate, maxDate)
  })

  const weekEnd = endOfWeek(weekStart, { weekStartsOn: 0 })
  const hasOffer =
    state.cart.groups[0].grooms.length > 0 ? state.cart.groups[0].grooms[0].offers[0]?.offer?.name : false

  const searchParams = useMemo(() => {
    if (!hasOffer) {
      return
    }
    return {
      petGroups: buildGroomPetGroup(bookingCtx.state, groomOffer),
      offersType: 'PRIMARY',
      offerName: state.cart.groups[0].grooms[0].offers[0].offer.name,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasOffer])

  useEffect(() => {
    if (isFetching.current) {
      return
    }

    if (isDataInCache(requestDates[0], requestDates[1], state, groomOffer.pet.id)) {
      setIsLoading(false)
      return
    }

    isFetching.current = true
    const req = async () => {
      const params = {
        ...searchParams,
        startDate: format(requestDates[0], 'yyyy-MM-dd'),
        endDate: format(requestDates[1], 'yyyy-MM-dd'),
      } as unknown as Types.TBookingSearchOptions
      try {
        const response = await Services.ClientBookingSearchService.searchByDays(locationName, 'grooming', params)
        bookingCtx.dispatch({
          type: 'SET_GROOM_AVAILABILITY',
          payload: {
            availability: response.results[0]?.availabilityGroupsByDays,
            startDate: requestDates[0],
            petId: groomOffer.pet.id,
          },
        })

        setIsLoading(false)
        isFetching.current = false
      } catch (error) {
        enqueueSnackbar(buildErrorMessage(error), { variant: 'error' })
        captureError(error as Error)
        isFetching.current = false
        setIsLoading(false)
      }
    }
    req()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationName, requestDates, serviceTypeName])

  const shouldDisableDate = useCallback(
    (day: Date) => {
      return !isDayAvailable(day, groomOffer, state, weekStart, weekEnd, checkProvider)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groomOffer, weekStart, weekEnd]
  )

  const currentDateSelected = useMemo(() => {
    const requestDate = groomOffer.requestDates[0]
    return requestDate && requestDate.date ? requestDate.date : null
  }, [groomOffer])

  const renderPickerDay = useCallback(
    (date: Date, _selectedDates: Date[], pickersDayProps: PickersDayProps<Date>) => {
      return (
        <CustomPickersDay
          {...pickersDayProps}
          selected={currentDateSelected ? currentDateSelected.getTime() === date.getTime() : false}
          data-current-week={isWithinInterval(date, { start: weekStart, end: weekEnd })}
        />
      )
    },
    [currentDateSelected, weekEnd, weekStart]
  )

  const onChangeDateValue = useCallback(
    (selectedDate: Date | null) => {
      if (!selectedDate) {
        enqueueSnackbar(t('requestDates.grooms.maxDays'), { variant: 'error' })
        return
      }

      bookingCtx.dispatch({
        type: 'SET_REQUEST_DATE',
        payload: {
          groupIdx: groomOffer.groupIdx,
          value: selectedDate,
          petIdx: groomOffer.pet.petIdx,
        },
      })

      const mustRequestTime = hasRequestTime(bookingCtx.state.data.inStayBookingConf)

      bookingCtx.dispatch({
        type: 'SET_GROOM_WIZARD_STATE',
        payload: {
          groupIdx: groomOffer.groupIdx,
          petIdx: groomOffer.pet.petIdx,
          state: 'dateSelector',
          isDone: !mustRequestTime,
        },
      })
    },

    [bookingCtx, groomOffer]
  )

  const handleDateOrMonthChange = (date: Date) => {
    const [fromDate, toDate] = getDateRange(date, minDate, maxDate)
    setRequestDates([fromDate, toDate])
    setWeekStart(date)
    if (isDataInCache(fromDate, toDate, state, groomOffer.pet.id)) {
      return
    }
    setIsLoading(true)
  }

  const monthButtons = isLoading
    ? { LeftArrowButton: () => <></>, RightArrowButton: () => <></> }
    : {
        LeftArrowButton: () => (
          <IconButton onClick={handlePrevWeek} disabled={weekStart.getTime() <= minDate.getTime()}>
            <NavigateBeforeIcon></NavigateBeforeIcon>
          </IconButton>
        ),
        RightArrowButton: () => (
          <IconButton onClick={handleNextWeek} disabled={weekEnd.getTime() >= maxDate.getTime()}>
            <NavigateNextIcon />
          </IconButton>
        ),
      }

  const handleNextWeek = () => {
    const next = addWeeks(weekStart, 1)
    handleDateOrMonthChange(next)
  }

  const handlePrevWeek = () => {
    const prev = subWeeks(weekStart, 1)
    handleDateOrMonthChange(prev)
  }

  return (
    <Box sx={{ maxHeight: '160px', minWidth: '320px', overflow: 'hidden' }}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <StaticDatePicker
          displayStaticWrapperAs="desktop"
          showToolbar={false}
          value={weekStart}
          minDate={minDate}
          maxDate={maxDate}
          loading={isLoading}
          renderLoading={() => <DayCalendarSkeleton singleWeekLoading />}
          shouldDisableDate={shouldDisableDate}
          onChange={onChangeDateValue}
          renderDay={renderPickerDay}
          renderInput={(_props) => <></>}
          views={['day']}
          components={monthButtons}
          showDaysOutsideCurrentMonth
        />
      </LocalizationProvider>
    </Box>
  )
}
